'use client';

import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
} from '@mui/material';
import {
  Gavel,
  LocalShipping,
  Security,
  Payment,
  Block,
  Warning,
  Policy,
  Description,
  CheckCircle,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { brandingConfig } from '@/config/branding';
import SimpleNav from '../../components/SimpleNav';
import { themeColors } from '../../theme/theme';
import { LayoutContainer } from '@/kit';
import { useTranslation } from '../../kit/useTranslation';

const COMPANY_NAME = brandingConfig.legal.companyName;
const LAST_UPDATED = 'December 7, 2025';

const Section = ({ icon: Icon, title, children }) => (
  <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 2, border: '1px solid #e5e7eb' }}>
    <Stack direction="row" spacing={1.5} alignItems="center" mb={2}>
      <Icon sx={{ color: themeColors.primaryColor }} />
      <Typography variant="h6" fontWeight={700} color="#1f2937">
        {title}
      </Typography>
    </Stack>
    {children}
  </Paper>
);

const BulletPoint = ({ children }) => (
  <ListItem sx={{ py: 0.5, pl: 0 }}>
    <ListItemIcon sx={{ minWidth: 28 }}>
      <CheckCircle sx={{ fontSize: 16, color: themeColors.primaryColor }} />
    </ListItemIcon>
    <ListItemText
      primary={children}
      primaryTypographyProps={{ fontSize: '0.9rem', color: '#4b5563' }}
    />
  </ListItem>
);

export default function TermsPage() {
  const isAuthenticated = useSelector((state) => state.auth?.isLoggedIn);
  const { t } = useTranslation();

  return (
    <LayoutContainer>
      <Box sx={{ minHeight: '100vh', bgcolor: '#fafafa' }}>
        {/* Simple Nav for non-authenticated users */}
        {!isAuthenticated && <SimpleNav />}

        <Box sx={{ pt: isAuthenticated ? 4 : 10, pb: 4 }}>
          <Container maxWidth="md">
            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Gavel sx={{ fontSize: 48, color: themeColors.primaryColor, mb: 2 }} />
              <Typography variant="h5" fontWeight={600} color="#0f172a" gutterBottom>
                {t('terms.title')}
              </Typography>
              <Typography variant="body2" color="#6b7280">
                {t('terms.lastUpdated', { date: LAST_UPDATED })}
              </Typography>
            </Box>

            {/* Introduction */}
            <Section icon={Description} title={t('terms.introduction')}>
              <Typography variant="body1" color="#4b5563" paragraph>
                {t('terms.introText1', { company: COMPANY_NAME })}
              </Typography>
              <Typography variant="body1" color="#4b5563">
                {t('terms.introText2')}
              </Typography>
            </Section>

            {/* Service Description */}
            <Section icon={LocalShipping} title={t('terms.services')}>
              <Typography variant="body1" color="#4b5563" paragraph>
                {t('terms.servicesIntro', { company: COMPANY_NAME })}
              </Typography>
              <List disablePadding>
                <BulletPoint>{t('terms.servicesList.pickup')}</BulletPoint>
                <BulletPoint>{t('terms.servicesList.tracking')}</BulletPoint>
                <BulletPoint>{t('terms.servicesList.otpVerified')}</BulletPoint>
                <BulletPoint>{t('terms.servicesList.multiLeg')}</BulletPoint>
                <BulletPoint>{t('terms.servicesList.businessAccounts')}</BulletPoint>
                <BulletPoint>{t('terms.servicesList.insurance')}</BulletPoint>
              </List>
            </Section>

            {/* Prohibited Items */}
            <Section icon={Block} title={t('terms.prohibitedItems')}>
              <Typography variant="body1" color="#4b5563" paragraph>
                {t('terms.prohibitedIntro')}
              </Typography>
              <List disablePadding>
                <BulletPoint>{t('terms.prohibitedList.hazardous')}</BulletPoint>
                <BulletPoint>{t('terms.prohibitedList.drugs')}</BulletPoint>
                <BulletPoint>{t('terms.prohibitedList.firearms')}</BulletPoint>
                <BulletPoint>{t('terms.prohibitedList.counterfeit')}</BulletPoint>
                <BulletPoint>{t('terms.prohibitedList.animals')}</BulletPoint>
                <BulletPoint>{t('terms.prohibitedList.currency')}</BulletPoint>
                <BulletPoint>{t('terms.prohibitedList.humanRemains')}</BulletPoint>
              </List>
              <Typography variant="body2" color="#dc2626" sx={{ mt: 2 }}>
                {t('terms.prohibitedWarning')}
              </Typography>
            </Section>

            {/* Liability & Insurance */}
            <Section icon={Security} title={t('terms.liability')}>
              <Typography variant="body1" color="#4b5563" paragraph>
                {t('terms.liabilityIntro')}
              </Typography>
              <List disablePadding>
                <BulletPoint>{t('terms.liabilityList.maxLiability')}</BulletPoint>
                <BulletPoint>{t('terms.liabilityList.additionalInsurance')}</BulletPoint>
                <BulletPoint>{t('terms.liabilityList.claimDeadline')}</BulletPoint>
                <BulletPoint>{t('terms.liabilityList.proofRequired')}</BulletPoint>
                <BulletPoint>{t('terms.liabilityList.forceMajeure')}</BulletPoint>
              </List>
            </Section>

            {/* Payment Terms */}
            <Section icon={Payment} title={t('terms.paymentTerms')}>
              <Typography variant="body1" color="#4b5563" paragraph>
                {t('terms.paymentIntro')}
              </Typography>
              <List disablePadding>
                <BulletPoint>{t('terms.paymentList.inr')}</BulletPoint>
                <BulletPoint>{t('terms.paymentList.prepaid')}</BulletPoint>
                <BulletPoint>{t('terms.paymentList.cod')}</BulletPoint>
                <BulletPoint>{t('terms.paymentList.refunds')}</BulletPoint>
                <BulletPoint>{t('terms.paymentList.credit')}</BulletPoint>
              </List>
            </Section>

            {/* User Responsibilities */}
            <Section icon={Warning} title={t('terms.userResponsibilities')}>
              <Typography variant="body1" color="#4b5563" paragraph>
                {t('terms.userResponsibilitiesIntro')}
              </Typography>
              <List disablePadding>
                <BulletPoint>{t('terms.userResponsibilitiesList.accurateAddress')}</BulletPoint>
                <BulletPoint>{t('terms.userResponsibilitiesList.properPackaging')}</BulletPoint>
                <BulletPoint>{t('terms.userResponsibilitiesList.declareValue')}</BulletPoint>
                <BulletPoint>{t('terms.userResponsibilitiesList.beAvailable')}</BulletPoint>
                <BulletPoint>{t('terms.userResponsibilitiesList.shareOtp')}</BulletPoint>
                <BulletPoint>{t('terms.userResponsibilitiesList.noMisuse')}</BulletPoint>
              </List>
            </Section>

            {/* Privacy Policy Link */}
            <Section icon={Policy} title={t('terms.privacyAndData')}>
              <Typography variant="body1" color="#4b5563" paragraph>
                {t('terms.privacyAndDataIntro')}
              </Typography>
              <List disablePadding>
                <BulletPoint>{t('terms.privacyAndDataList.personalInfo')}</BulletPoint>
                <BulletPoint>{t('terms.privacyAndDataList.locationData')}</BulletPoint>
                <BulletPoint>{t('terms.privacyAndDataList.secureStorage')}</BulletPoint>
                <BulletPoint>{t('terms.privacyAndDataList.noSharing')}</BulletPoint>
                <BulletPoint>{t('terms.privacyAndDataList.rightToDelete')}</BulletPoint>
              </List>
              <Typography variant="body2" color="#6b7280" sx={{ mt: 2 }}>
                {t('terms.privacyRef')}
              </Typography>
            </Section>

            {/* Dispute Resolution */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                mb: 3,
                borderRadius: 2,
                border: '1px solid #e5e7eb',
                bgcolor: '#fefce8',
              }}
            >
              <Typography variant="h6" fontWeight={700} color="#1f2937" mb={2}>
                {t('terms.disputeResolution')}
              </Typography>
              <Typography variant="body1" color="#4b5563" paragraph>
                {t('terms.disputeIntro')}
              </Typography>
              <Typography variant="body1" color="#4b5563" component="ol" sx={{ pl: 2 }}>
                <li>{t('terms.disputeSteps.step1')}</li>
                <li>{t('terms.disputeSteps.step2')}</li>
                <li>{t('terms.disputeSteps.step3')}</li>
                <li>{t('terms.disputeSteps.step4')}</li>
              </Typography>
            </Paper>

            {/* Contact */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 2,
                border: '1px solid #e5e7eb',
                textAlign: 'center',
              }}
            >
              <Typography variant="h6" fontWeight={700} color="#1f2937" mb={1}>
                {t('terms.questions')}
              </Typography>
              <Typography variant="body1" color="#4b5563">
                {t('terms.questionsText')}
              </Typography>
              <Typography variant="body1" color={themeColors.primaryColor} fontWeight={600} mt={1}>
                {brandingConfig.contact.supportEmail}
              </Typography>
            </Paper>
          </Container>
        </Box>
      </Box>
    </LayoutContainer>
  );
}
